import { useEffect, useState } from 'react';
import { Accordion, Button } from '@ab-inbev-maz/compass';
import { useCanvasEditor } from './CanvasEditorContext';
import { useFonts } from '@/hooks/canvas/useFonts';
import type { FontFamily, FontVariant } from '@/services/fontsService';
import { formatFontName, loadFontVariant } from '@/utils/fontLoader';

// Canvas text objects store the variant id as `fontFamily`; the weight comes from the font file itself.
const cssFontFamily = (variant: FontVariant) => `"${variant.id}"`;

interface CanvasTextTabProps {
  search?: string;
}

// "Text" panel: create text on the canvas and apply a font variant to the selected text.
const CanvasTextTab = ({ search = '' }: CanvasTextTabProps) => {
  const { addText, applyTextStyle } = useCanvasEditor();
  const { fonts, isLoading, error } = useFonts(search);
  const [expandedFontId, setExpandedFontId] = useState<string | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);

  // Preloads each family's first variant so its title can be previewed in its own font.
  useEffect(() => {
    fonts.forEach((font) => {
      if (font.variants[0]) loadFontVariant(font.variants[0]).catch(() => undefined);
    });
  }, [fonts]);

  const handleExpand = (font: FontFamily, open: unknown) => {
    setExpandedFontId(open ? font.id : null);
    if (open) font.variants.forEach((variant) => loadFontVariant(variant).catch(() => undefined));
  };

  const handleSelectVariant = async (variant: FontVariant) => {
    setApplyError(null);
    try {
      await loadFontVariant(variant);
      applyTextStyle({ fontFamily: variant.id, fontUrl: variant.url, fontWeight: 400 });
    } catch {
      setApplyError('No se pudo cargar la fuente seleccionada.');
    }
  };

  const renderFonts = () => {
    if (isLoading) return <p className="text-sm text-gray-500">Cargando fuentes...</p>;
    if (error) return <p className="text-sm text-red-600">{error}</p>;
    if (fonts.length === 0) return <p className="text-sm text-gray-500">No se encontraron fuentes.</p>;

    return fonts.map((font) => (
      <Accordion
        key={font.id}
        open={expandedFontId === font.id}
        onChange={(open) => handleExpand(font, open)}
        title={
          <span
            style={font.variants[0] ? { fontFamily: cssFontFamily(font.variants[0]) } : undefined}
            className="text-lg font-semibold text-gray-900"
          >
            {formatFontName(font.name)} <span className="text-gray-400 font-normal">AaBbCc</span>
          </span>
        }
        className="[--accordion-border-bottom:none] px-5 py-3 bg-zinc-50 rounded-xl"
      >
        <div className="flex flex-col max-h-56 overflow-y-auto">
          {font.variants.map((variant) => (
            <button
              key={variant.id}
              type="button"
              onClick={() => handleSelectVariant(variant)}
              style={{ fontFamily: cssFontFamily(variant) }}
              className="px-4 py-1 text-left text-sm text-gray-700 cursor-pointer hover:bg-gray-50"
            >
              {formatFontName(variant.name)}
            </button>
          ))}
        </div>
      </Accordion>
    ));
  };

  return (
    <div className="flex flex-col gap-3">
      <Button
        label="Crear texto"
        variant="primary"
        className="w-full cursor-pointer rounded-full"
        onClick={addText}
      />

      {applyError && <p className="text-sm text-red-600">{applyError}</p>}

      <div className="flex flex-col gap-2">{renderFonts()}</div>
    </div>
  );
};

export default CanvasTextTab;

